import React, { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ProgressRing } from "../app/ui";
import { isLowPowerClient } from "../../lib/platform";

const PINK = "#E7A6C4";
const STAGES = [
  { label: "First trimester", weeks: "Weeks 1 – 13", size: "the size of a plum", color: PINK,
    text: "Nausea and deep fatigue are real. Rest, small snacks and gentle walks. Your partner learns to quietly take things off your plate." },
  { label: "Second trimester", weeks: "Weeks 14 – 27", size: "the size of a banana", color: "#7A9E7E",
    text: "Energy returns. Prenatal strength, swimming and planning ahead. Baby can hear your voices now — so your partner starts talking to the bump." },
  { label: "Third trimester", weeks: "Weeks 28 – 40", size: "the size of a small pumpkin", color: "#D4924A",
    text: "Baby moves into position. Short walks, breathing practice and nesting. Your partner learns the birth plan and the route." },
  { label: "Postpartum", weeks: "The fourth trimester", size: "finally in your arms", color: PINK,
    text: "Healing and bonding. Rest when baby rests; gentle recovery once you're cleared. Your partner takes the night feeds and protects your rest." },
];

export function TrimesterTimeline() {
  const [lite] = useState(() => isLowPowerClient());

  // Mobile / reduced-motion: a lightweight static list (no sticky scroll, no
  // scroll subscription, no GPU cost).
  if (lite) {
    return (
      <section id="journey" className="relative mx-auto max-w-3xl px-6 py-20" data-testid="trimester-timeline">
        <div className="text-center">
          <p className="eyebrow">The journey, week by week</p>
          <h2 className="mt-4 font-display text-3xl font-semibold text-mist sm:text-4xl">Every stage, guided</h2>
        </div>
        <div className="mt-10 space-y-4">
          {STAGES.map((st) => (
            <div key={st.label} className="glass rounded-2xl p-5" data-testid="timeline-stage-label">
              <div className="flex items-center gap-3">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: st.color }} />
                <h3 className="font-display text-xl font-semibold" style={{ color: st.color }}>{st.label}</h3>
                <span className="ml-auto text-xs uppercase tracking-wider text-mist-muted">{st.weeks}</span>
              </div>
              <p className="mt-1.5 text-sm text-mist-muted"><span className="text-mist">Baby is {st.size}.</span> {st.text}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }
  return <TrimesterTimelineFull />;
}

// Desktop sticky scroll-morph — only mounted on capable devices.
function TrimesterTimelineFull() {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const i = Math.min(STAGES.length - 1, Math.max(0, Math.floor(p * STAGES.length)));
    if (i !== idx) setIdx(i);
  });
  const s = STAGES[idx];

  return (
    <section ref={ref} id="journey" className="relative" style={{ height: "280vh" }} data-testid="trimester-timeline">
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[160px] transition-colors duration-700" style={{ background: `${s.color}1F` }} />
        <div className="relative mx-auto grid w-full max-w-6xl items-center gap-10 px-6 lg:grid-cols-2 lg:px-8">
          {/* Visual */}
          <div className="flex justify-center">
            <motion.div key={idx} initial={{ scale: 0.92, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <ProgressRing value={idx + 1} max={STAGES.length} size={240} stroke={14} color={s.color}>
                <span className="px-6 text-center font-display text-xl font-bold leading-tight text-mist">{s.size}</span>
              </ProgressRing>
            </motion.div>
          </div>
          {/* Copy */}
          <div>
            <p className="eyebrow">The journey, week by week</p>
            <motion.h2 key={`h-${idx}`} initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}
              className="mt-4 font-display text-4xl font-semibold text-mist sm:text-5xl" style={{ color: s.color }} data-testid="timeline-stage-label">
              {s.label}
            </motion.h2>
            <p className="mt-2 text-sm uppercase tracking-wider text-mist-muted">{s.weeks}</p>
            <motion.p key={`p-${idx}`} initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-6 max-w-md text-base leading-relaxed text-mist-muted">
              {s.text}
            </motion.p>
            {/* progress dots */}
            <div className="mt-8 flex gap-2">
              {STAGES.map((st, i) => (
                <span key={i} className="h-1.5 rounded-full transition-all duration-500" style={{ width: i === idx ? 40 : 16, background: i <= idx ? st.color : "rgba(255,255,255,0.15)" }} />
              ))}
            </div>
            <p className="mt-6 text-xs text-mist-muted">Scroll to move through your pregnancy →</p>
          </div>
        </div>
      </div>
    </section>
  );
}
